import { useState, useEffect, useMemo, useCallback } from 'react';
import { apiClient } from '../lib/api-client';
import { langName } from '../lib/lang-names';

interface GeoFeature {
  type: 'Feature';
  geometry: { type: 'Point'; coordinates: [number, number] };
  properties: { c: string; n: string; f: string; e: string; m: string; g?: string };
}

interface GeoJSON {
  type: 'FeatureCollection';
  features: GeoFeature[];
}

interface LanguageProfile {
  language_code: string;
  name: string;
  glottocode: string;
  family: string;
  macroarea: string;
  classification: string;
  endangerment: string;
  countries: string;
  coordinates: { lat: number; lng: number };
  phoneme_inventory?: {
    total: number;
    consonants: number;
    vowels: number;
    tones: number;
    consonant_list: string[];
    vowel_list: string[];
    tone_list: string[];
  };
  wals_count: number;
  grambank_count: number;
  lexicon_entries: number;
}

interface LexiconItem {
  word: string;
  pos?: string;
  ipa?: string;
  definition?: string;
  definitions?: Record<string, string>;
  senses?: string[];
  lemma_id?: string;
  rank?: number;
}

type Tab = 'dashboard' | 'languages' | 'data-builder';

const ENDANGERMENT_CONFIG: Record<string, { label: string; color: string; bg: string; dot: string }> = {
  'not endangered': { label: 'Not Endangered', color: 'text-teal-400', bg: 'bg-teal-900/20 border-teal-800/50', dot: 'bg-teal-400' },
  'shifting': { label: 'Shifting', color: 'text-amber-400', bg: 'bg-amber-900/20 border-amber-800/50', dot: 'bg-amber-400' },
  'threatened': { label: 'Threatened', color: 'text-orange-400', bg: 'bg-orange-900/20 border-orange-800/50', dot: 'bg-orange-400' },
  'moribund': { label: 'Moribund', color: 'text-red-400', bg: 'bg-red-900/20 border-red-800/50', dot: 'bg-red-400' },
  'nearly extinct': { label: 'Nearly Extinct', color: 'text-red-300', bg: 'bg-red-900/30 border-red-700/50', dot: 'bg-red-300' },
  'extinct': { label: 'Extinct', color: 'text-zinc-500', bg: 'bg-zinc-800/30 border-zinc-700/50', dot: 'bg-zinc-500' },
};

const MACROAREA_ORDER = ['Eurasia', 'Africa', 'Papunesia', 'South America', 'North America', 'Australia'];

const PAGE_SIZE = 50;

export default function Atlas() {
  const [tab, setTab] = useState<Tab>('dashboard');
  const [geo, setGeo] = useState<GeoJSON | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadGeoData();
  }, []);

  const loadGeoData = async () => {
    try {
      setLoading(true);
      const resp = await fetch('/atlas-languages.json');
      if (!resp.ok) throw new Error(`Failed to load: ${resp.status}`);
      const data: GeoJSON = await resp.json();
      setGeo(data);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load language data');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="p-4 md:p-8">
        <h1 className="text-xl md:text-2xl font-semibold text-white mb-4">Atlas</h1>
        <p className="text-zinc-500">Loading 7,694 languages...</p>
      </div>
    );
  }

  if (error || !geo) {
    return (
      <div className="p-4 md:p-8">
        <h1 className="text-xl md:text-2xl font-semibold text-white mb-4">Atlas</h1>
        <p className="text-red-400">{error}</p>
        <button onClick={loadGeoData} className="mt-2 px-3 py-1 bg-zinc-700 rounded text-sm text-zinc-300 hover:bg-zinc-600">
          Retry
        </button>
      </div>
    );
  }

  const tabs: { key: Tab; label: string }[] = [
    { key: 'dashboard', label: 'Dashboard' },
    { key: 'languages', label: 'Languages' },
    { key: 'data-builder', label: 'Data Builder' },
  ];

  return (
    <div className="p-4 md:p-8 space-y-4 md:space-y-6">
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-xl md:text-2xl font-semibold text-white">Atlas</h1>
          <p className="text-zinc-500 text-sm mt-1">
            Interactive linguistic museum — {geo.features.length.toLocaleString()} languages mapped
          </p>
        </div>
        <a
          href="https://glossika.com/atlas/"
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-2 px-3 py-1.5 bg-teal-700 rounded text-sm text-white hover:bg-teal-600 w-fit"
        >
          Open Public Atlas
          <span className="text-xs opacity-70">&#8599;</span>
        </a>
      </div>

      <div className="flex gap-1 border-b border-zinc-800">
        {tabs.map(t => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
              tab === t.key
                ? 'border-teal-400 text-teal-400'
                : 'border-transparent text-zinc-500 hover:text-zinc-300'
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'dashboard' && <DashboardTab geo={geo} />}
      {tab === 'languages' && <LanguagesTab geo={geo} />}
      {tab === 'data-builder' && <DataBuilderTab geo={geo} />}
    </div>
  );
}

function DashboardTab({ geo }: { geo: GeoJSON }) {
  const stats = useMemo(() => {
    const byEndangerment: Record<string, number> = {};
    const byMacroarea: Record<string, number> = {};
    const byFamily: Record<string, number> = {};

    for (const f of geo.features) {
      const e = f.properties.e || 'unknown';
      byEndangerment[e] = (byEndangerment[e] || 0) + 1;

      const m = f.properties.m || 'Unknown';
      byMacroarea[m] = (byMacroarea[m] || 0) + 1;

      const fam = f.properties.f || 'Unclassified';
      byFamily[fam] = (byFamily[fam] || 0) + 1;
    }

    const familyCount = Object.keys(byFamily).length;

    const topFamilies = Object.entries(byFamily)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 20);

    return { byEndangerment, byMacroarea, topFamilies, familyCount };
  }, [geo]);

  const endangeredCount = geo.features.filter(f =>
    f.properties.e && f.properties.e !== 'not endangered'
  ).length;

  return (
    <div className="space-y-6">
      {/* Summary cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div className="border border-zinc-800 rounded-lg p-3 md:p-4">
          <div className="text-xs uppercase tracking-wider text-zinc-500">Total Languages</div>
          <div className="text-2xl font-semibold text-white mt-1">{geo.features.length.toLocaleString()}</div>
          <div className="text-xs text-zinc-500 mt-1">From Glottolog geoboxes</div>
        </div>
        <div className="border border-zinc-800 rounded-lg p-3 md:p-4">
          <div className="text-xs uppercase tracking-wider text-zinc-500">Language Families</div>
          <div className="text-2xl font-semibold text-white mt-1">{stats.familyCount}</div>
          <div className="text-xs text-zinc-500 mt-1">Including isolates</div>
        </div>
        <div className="border border-zinc-800 rounded-lg p-3 md:p-4">
          <div className="text-xs uppercase tracking-wider text-amber-400">At Risk</div>
          <div className="text-2xl font-semibold text-amber-400 mt-1">{endangeredCount.toLocaleString()}</div>
          <div className="text-xs text-zinc-500 mt-1">Shifting through extinct</div>
        </div>
        <div className="border border-zinc-800 rounded-lg p-3 md:p-4">
          <div className="text-xs uppercase tracking-wider text-zinc-500">Macroareas</div>
          <div className="text-2xl font-semibold text-white mt-1">{Object.keys(stats.byMacroarea).length}</div>
          <div className="text-xs text-zinc-500 mt-1">Global coverage</div>
        </div>
      </div>

      {/* Endangerment breakdown */}
      <div>
        <h3 className="text-sm font-medium text-zinc-300 mb-3">Endangerment Status</h3>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-2">
          {Object.entries(ENDANGERMENT_CONFIG).map(([key, cfg]) => {
            const count = stats.byEndangerment[key] || 0;
            const pct = geo.features.length > 0 ? ((count / geo.features.length) * 100).toFixed(1) : '0';
            return (
              <div key={key} className={`border rounded-lg p-3 ${cfg.bg}`}>
                <div className="flex items-center gap-2 mb-1">
                  <span className={`w-2 h-2 rounded-full ${cfg.dot}`} />
                  <span className={`text-xs font-medium ${cfg.color}`}>{cfg.label}</span>
                </div>
                <div className="text-lg font-semibold text-white">{count.toLocaleString()}</div>
                <div className="text-xs text-zinc-500">{pct}%</div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Macroarea breakdown */}
      <div>
        <h3 className="text-sm font-medium text-zinc-300 mb-3">By Macroarea</h3>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-2">
          {MACROAREA_ORDER.map(area => {
            const count = stats.byMacroarea[area] || 0;
            return (
              <div key={area} className="border border-zinc-800 rounded-lg p-3">
                <div className="text-xs text-zinc-400 mb-1">{area}</div>
                <div className="text-lg font-semibold text-white">{count.toLocaleString()}</div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Top families */}
      <div>
        <h3 className="text-sm font-medium text-zinc-300 mb-3">Top 20 Language Families</h3>
        <div className="overflow-x-auto">
          <table className="w-full min-w-[400px]">
            <thead>
              <tr className="border-b border-zinc-800">
                <th className="text-left text-xs text-zinc-500 uppercase tracking-wider py-2 pr-4">#</th>
                <th className="text-left text-xs text-zinc-500 uppercase tracking-wider py-2 pr-4">Family</th>
                <th className="text-right text-xs text-zinc-500 uppercase tracking-wider py-2 pr-4">Languages</th>
                <th className="text-left text-xs text-zinc-500 uppercase tracking-wider py-2">Share</th>
              </tr>
            </thead>
            <tbody>
              {stats.topFamilies.map(([family, count], i) => {
                const pct = (count / geo.features.length) * 100;
                return (
                  <tr key={family} className="border-b border-zinc-800/50 hover:bg-zinc-800/20">
                    <td className="text-zinc-600 text-sm py-1.5 pr-4">{i + 1}</td>
                    <td className="text-zinc-200 text-sm py-1.5 pr-4">{family}</td>
                    <td className="text-zinc-300 text-sm py-1.5 pr-4 text-right font-mono">{count.toLocaleString()}</td>
                    <td className="py-1.5">
                      <div className="flex items-center gap-2">
                        <div className="w-24 h-1.5 bg-zinc-800 rounded-full overflow-hidden">
                          <div
                            className="h-full bg-teal-500/60 rounded-full"
                            style={{ width: `${Math.min(pct, 100)}%` }}
                          />
                        </div>
                        <span className="text-xs text-zinc-500">{pct.toFixed(1)}%</span>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

type SortKey = 'c' | 'n' | 'f' | 'e' | 'm';
type SortDir = 'asc' | 'desc';

function SortHeader({ label, sortKey, currentSort, currentDir, onSort }: {
  label: string; sortKey: SortKey;
  currentSort: SortKey; currentDir: SortDir;
  onSort: (k: SortKey) => void;
}) {
  const active = currentSort === sortKey;
  return (
    <th
      onClick={() => onSort(sortKey)}
      className="text-left text-xs text-zinc-500 uppercase tracking-wider py-2 pr-3 cursor-pointer select-none hover:text-zinc-300"
    >
      {label}
      {active && <span className="ml-1">{currentDir === 'asc' ? '▲' : '▼'}</span>}
    </th>
  );
}

function LanguagesTab({ geo }: { geo: GeoJSON }) {
  const [search, setSearch] = useState('');
  const [filterEndangerment, setFilterEndangerment] = useState<string>('');
  const [filterMacroarea, setFilterMacroarea] = useState<string>('');
  const [filterFamily, setFilterFamily] = useState<string>('');
  const [sortKey, setSortKey] = useState<SortKey>('n');
  const [sortDir, setSortDir] = useState<SortDir>('asc');
  const [page, setPage] = useState(0);
  const [selectedCode, setSelectedCode] = useState<string | null>(null);

  const families = useMemo(() => {
    const fams = new Set<string>();
    for (const f of geo.features) {
      if (f.properties.f) fams.add(f.properties.f);
    }
    return [...fams].sort();
  }, [geo]);

  const handleSort = (key: SortKey) => {
    if (sortKey === key) {
      setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setSortKey(key);
      setSortDir('asc');
    }
    setPage(0);
  };

  const filtered = useMemo(() => {
    const q = search.toLowerCase().trim();
    const list = geo.features.filter(f => {
      const p = f.properties;
      if (q && !p.n.toLowerCase().includes(q) && !p.c.toLowerCase().includes(q)) return false;
      if (filterEndangerment && p.e !== filterEndangerment) return false;
      if (filterMacroarea && p.m !== filterMacroarea) return false;
      if (filterFamily && p.f !== filterFamily) return false;
      return true;
    });
    list.sort((a, b) => {
      const av = (a.properties[sortKey] || '').toLowerCase();
      const bv = (b.properties[sortKey] || '').toLowerCase();
      const cmp = av < bv ? -1 : av > bv ? 1 : 0;
      return sortDir === 'asc' ? cmp : -cmp;
    });
    return list;
  }, [geo, search, filterEndangerment, filterMacroarea, filterFamily, sortKey, sortDir]);

  useEffect(() => { setPage(0); }, [search, filterEndangerment, filterMacroarea, filterFamily]);

  const pageCount = Math.ceil(filtered.length / PAGE_SIZE);
  const pageItems = filtered.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);

  return (
    <div className="space-y-4">
      {/* Filters */}
      <div className="flex flex-wrap gap-3">
        <input
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder="Search by name or code..."
          className="w-full sm:w-64 px-3 py-1.5 bg-zinc-800 border border-zinc-700 rounded text-sm text-white placeholder-zinc-500 focus:border-teal-600 focus:outline-none"
        />
        <select
          value={filterEndangerment}
          onChange={e => setFilterEndangerment(e.target.value)}
          className="w-full sm:w-auto px-3 py-1.5 bg-zinc-800 border border-zinc-700 rounded text-sm text-white focus:border-teal-600 focus:outline-none"
        >
          <option value="">All statuses</option>
          {Object.entries(ENDANGERMENT_CONFIG).map(([key, cfg]) => (
            <option key={key} value={key}>{cfg.label}</option>
          ))}
        </select>
        <select
          value={filterMacroarea}
          onChange={e => setFilterMacroarea(e.target.value)}
          className="w-full sm:w-auto px-3 py-1.5 bg-zinc-800 border border-zinc-700 rounded text-sm text-white focus:border-teal-600 focus:outline-none"
        >
          <option value="">All macroareas</option>
          {MACROAREA_ORDER.map(a => (
            <option key={a} value={a}>{a}</option>
          ))}
        </select>
        <select
          value={filterFamily}
          onChange={e => setFilterFamily(e.target.value)}
          className="w-full sm:w-auto px-3 py-1.5 bg-zinc-800 border border-zinc-700 rounded text-sm text-white focus:border-teal-600 focus:outline-none"
        >
          <option value="">All families ({families.length})</option>
          {families.map(f => (
            <option key={f} value={f}>{f}</option>
          ))}
        </select>
      </div>

      <div className="text-xs text-zinc-500">
        {filtered.length.toLocaleString()} languages
        {filtered.length !== geo.features.length && ` (of ${geo.features.length.toLocaleString()})`}
      </div>

      {/* Table */}
      <div className="overflow-x-auto">
        <table className="w-full min-w-[600px]">
          <thead>
            <tr className="border-b border-zinc-800">
              <SortHeader label="Code" sortKey="c" currentSort={sortKey} currentDir={sortDir} onSort={handleSort} />
              <SortHeader label="Name" sortKey="n" currentSort={sortKey} currentDir={sortDir} onSort={handleSort} />
              <SortHeader label="Family" sortKey="f" currentSort={sortKey} currentDir={sortDir} onSort={handleSort} />
              <SortHeader label="Area" sortKey="m" currentSort={sortKey} currentDir={sortDir} onSort={handleSort} />
              <SortHeader label="Status" sortKey="e" currentSort={sortKey} currentDir={sortDir} onSort={handleSort} />
              <th className="text-right text-xs text-zinc-500 uppercase tracking-wider py-2">Coords</th>
            </tr>
          </thead>
          <tbody>
            {pageItems.map(f => {
              const p = f.properties;
              const eCfg = ENDANGERMENT_CONFIG[p.e];
              return (
                <tr
                  key={p.c}
                  onClick={() => setSelectedCode(selectedCode === p.c ? null : p.c)}
                  className="border-b border-zinc-800/50 hover:bg-zinc-800/30 cursor-pointer transition-colors"
                >
                  <td className="text-teal-400 text-sm py-2 pr-3 font-mono">{p.c}</td>
                  <td className="text-zinc-200 text-sm py-2 pr-3">{p.n}</td>
                  <td className="text-zinc-400 text-sm py-2 pr-3 max-w-[200px] truncate">{p.f}</td>
                  <td className="text-zinc-400 text-sm py-2 pr-3">{p.m}</td>
                  <td className="py-2 pr-3">
                    <span className={`inline-flex items-center gap-1.5 text-xs ${eCfg?.color || 'text-zinc-400'}`}>
                      <span className={`w-1.5 h-1.5 rounded-full ${eCfg?.dot || 'bg-zinc-500'}`} />
                      {eCfg?.label || p.e || 'Unknown'}
                    </span>
                  </td>
                  <td className="text-zinc-500 text-xs py-2 text-right font-mono">
                    {f.geometry.coordinates[1].toFixed(2)}, {f.geometry.coordinates[0].toFixed(2)}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {pageCount > 1 && (
        <div className="flex flex-wrap items-center gap-2 justify-between">
          <button
            onClick={() => setPage(p => Math.max(0, p - 1))}
            disabled={page === 0}
            className="px-3 py-1 bg-zinc-700 rounded text-sm text-zinc-300 hover:bg-zinc-600 disabled:opacity-30"
          >
            Previous
          </button>
          <span className="text-xs text-zinc-500">
            Page {page + 1} of {pageCount}
          </span>
          <button
            onClick={() => setPage(p => Math.min(pageCount - 1, p + 1))}
            disabled={page >= pageCount - 1}
            className="px-3 py-1 bg-zinc-700 rounded text-sm text-zinc-300 hover:bg-zinc-600 disabled:opacity-30"
          >
            Next
          </button>
        </div>
      )}

      {/* Detail panel */}
      {selectedCode && (
        <LanguageDetail code={selectedCode} onClose={() => setSelectedCode(null)} />
      )}
    </div>
  );
}

const LETTERS = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'.split('');

function LexiconBrowser({ code, name }: { code: string; name: string }) {
  const [items, setItems] = useState<LexiconItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [activeLetter, setActiveLetter] = useState<string | null>(null);
  const [prefix, setPrefix] = useState('');
  const [expandedIdx, setExpandedIdx] = useState<number | null>(null);

  const browse = useCallback(async (pfx: string) => {
    if (!pfx) { setItems([]); return; }
    try {
      setLoading(true);
      setError(null);
      setExpandedIdx(null);
      const encoded = encodeURIComponent(pfx.toLowerCase());
      const data = await apiClient<{ items: LexiconItem[]; count: number }>(
        `/api/v1/lexicon/${code}/${encoded}?browse=1&limit=200`
      );
      setItems(data.items || []);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to browse lexicon');
    } finally {
      setLoading(false);
    }
  }, [code]);

  const handleLetterClick = (letter: string) => {
    setActiveLetter(letter);
    setPrefix('');
    browse(letter);
  };

  const handlePrefixSubmit = () => {
    const p = prefix.trim();
    if (!p) return;
    setActiveLetter(null);
    browse(p);
  };

  return (
    <div className="mt-4 border border-zinc-700 rounded-lg p-4">
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between mb-3">
        <h4 className="text-sm font-medium text-zinc-200">Lexicon — {name} ({code})</h4>
        {items.length > 0 && <span className="text-xs text-zinc-500">{items.length} entries</span>}
      </div>

      <div className="flex flex-wrap gap-1 mb-3">
        {LETTERS.map(l => (
          <button
            key={l}
            onClick={() => handleLetterClick(l)}
            className={`w-7 h-7 rounded text-xs font-mono transition-colors ${
              activeLetter === l
                ? 'bg-teal-600 text-white'
                : 'bg-zinc-800 text-zinc-400 hover:bg-zinc-700 hover:text-zinc-200'
            }`}
          >
            {l}
          </button>
        ))}
      </div>

      <div className="flex flex-wrap gap-2 mb-3">
        <input
          value={prefix}
          onChange={e => setPrefix(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && handlePrefixSubmit()}
          placeholder="Or type a prefix for non-Latin scripts..."
          className="w-full sm:w-72 px-3 py-1.5 bg-zinc-800 border border-zinc-700 rounded text-sm text-white placeholder-zinc-500 focus:border-teal-600 focus:outline-none"
        />
        <button
          onClick={handlePrefixSubmit}
          disabled={loading || !prefix.trim()}
          className="px-3 py-1.5 bg-teal-700 rounded text-sm text-white hover:bg-teal-600 disabled:opacity-30"
        >
          Browse
        </button>
      </div>

      {error && <p className="text-red-400 text-sm mb-2">{error}</p>}
      {loading && <p className="text-zinc-500 text-xs py-2">Loading...</p>}

      {!activeLetter && !prefix && !loading && items.length === 0 && (
        <p className="text-zinc-500 text-sm py-4 text-center">Click a letter or type a prefix to browse entries</p>
      )}

      {items.length > 0 && (
        <div className="max-h-[500px] overflow-y-auto space-y-px">
          {items.map((item, i) => {
            const isExpanded = expandedIdx === i;
            const gloss = item.definition || item.senses?.join(', ') || '';
            return (
              <div key={`${item.word}-${item.pos}-${i}`}>
                <button
                  onClick={() => setExpandedIdx(isExpanded ? null : i)}
                  className={`w-full text-left px-3 py-2 rounded transition-colors flex items-center gap-3 ${
                    isExpanded ? 'bg-zinc-700/50' : 'hover:bg-zinc-800/50'
                  }`}
                >
                  <span className="text-teal-400 font-mono text-sm min-w-0 shrink-0">{item.word}</span>
                  {item.pos && (
                    <span className="text-zinc-500 text-xs shrink-0 bg-zinc-800 px-1.5 py-0.5 rounded">{item.pos}</span>
                  )}
                  <span className="text-zinc-400 text-xs truncate min-w-0">{gloss}</span>
                  <svg
                    className={`w-3.5 h-3.5 text-zinc-500 shrink-0 ml-auto transition-transform ${isExpanded ? 'rotate-180' : ''}`}
                    fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
                {isExpanded && (
                  <div className="px-3 py-3 ml-3 border-l-2 border-teal-800/40 space-y-2">
                    {item.ipa && (
                      <div>
                        <span className="text-xs text-zinc-500 uppercase tracking-wider">Pronunciation</span>
                        <p className="text-zinc-200 font-mono text-sm mt-0.5">{item.ipa}</p>
                      </div>
                    )}
                    {gloss && (
                      <div>
                        <span className="text-xs text-zinc-500 uppercase tracking-wider">Definition</span>
                        <p className="text-zinc-200 text-sm mt-0.5">{gloss}</p>
                      </div>
                    )}
                    {item.definitions && Object.keys(item.definitions).length > 0 && (
                      <div>
                        <span className="text-xs text-zinc-500 uppercase tracking-wider">Translations</span>
                        <div className="mt-1 grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-0.5 max-h-[200px] overflow-y-auto">
                          {Object.entries(item.definitions).slice(0, 20).map(([lang, def]) => (
                            <div key={lang} className="flex gap-2 text-xs">
                              <span className="text-zinc-500 font-mono shrink-0 w-8">{lang}</span>
                              <span className="text-zinc-300 truncate">{def}</span>
                            </div>
                          ))}
                          {Object.keys(item.definitions).length > 20 && (
                            <span className="text-zinc-600 text-xs col-span-full">
                              +{Object.keys(item.definitions).length - 20} more languages
                            </span>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {(activeLetter || prefix) && items.length === 0 && !loading && !error && (
        <p className="text-zinc-500 text-sm py-4 text-center">No entries found</p>
      )}
    </div>
  );
}

function LanguageDetail({ code, onClose }: { code: string; onClose: () => void }) {
  const [profile, setProfile] = useState<LanguageProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showLexicon, setShowLexicon] = useState(false);

  const loadProfile = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await apiClient<LanguageProfile>(`/api/v1/languages/${code}/profile`);
      setProfile(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load profile');
    } finally {
      setLoading(false);
    }
  }, [code]);

  useEffect(() => { loadProfile(); setShowLexicon(false); }, [loadProfile]);

  return (
    <div className="border border-teal-800/40 bg-teal-900/10 rounded-lg p-4 md:p-5">
      <div className="flex items-start justify-between mb-4">
        <div>
          <h3 className="text-lg font-semibold text-white">
            {profile?.name || code}
            <span className="text-teal-400 font-mono text-sm ml-2">{code}</span>
          </h3>
          {profile?.glottocode && (
            <span className="text-zinc-500 text-xs">Glottocode: {profile.glottocode}</span>
          )}
        </div>
        <button onClick={onClose} className="text-zinc-500 hover:text-zinc-300 text-sm">Close</button>
      </div>

      {loading && <p className="text-zinc-500 text-sm">Loading profile...</p>}
      {error && (
        <div>
          <p className="text-red-400 text-sm">{error}</p>
          <button onClick={loadProfile} className="mt-1 text-xs text-zinc-400 hover:text-zinc-200">Retry</button>
        </div>
      )}

      {profile && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
          <div>
            <div className="text-xs text-zinc-500 uppercase tracking-wider">Family</div>
            <div className="text-zinc-200 mt-0.5">{profile.family || 'Unknown'}</div>
          </div>
          <div>
            <div className="text-xs text-zinc-500 uppercase tracking-wider">Macroarea</div>
            <div className="text-zinc-200 mt-0.5">{profile.macroarea || 'Unknown'}</div>
          </div>
          <div>
            <div className="text-xs text-zinc-500 uppercase tracking-wider">Status</div>
            <div className={`mt-0.5 ${ENDANGERMENT_CONFIG[profile.endangerment]?.color || 'text-zinc-400'}`}>
              {ENDANGERMENT_CONFIG[profile.endangerment]?.label || profile.endangerment || 'Unknown'}
            </div>
          </div>
          <div>
            <div className="text-xs text-zinc-500 uppercase tracking-wider">Countries</div>
            <div className="text-zinc-200 mt-0.5 font-mono text-xs">{profile.countries || 'N/A'}</div>
          </div>

          {profile.phoneme_inventory && (
            <>
              <div>
                <div className="text-xs text-zinc-500 uppercase tracking-wider">Phonemes</div>
                <div className="text-zinc-200 mt-0.5">{profile.phoneme_inventory.total}</div>
              </div>
              <div>
                <div className="text-xs text-zinc-500 uppercase tracking-wider">Consonants</div>
                <div className="text-zinc-200 mt-0.5">{profile.phoneme_inventory.consonants}</div>
              </div>
              <div>
                <div className="text-xs text-zinc-500 uppercase tracking-wider">Vowels</div>
                <div className="text-zinc-200 mt-0.5">{profile.phoneme_inventory.vowels}</div>
              </div>
              <div>
                <div className="text-xs text-zinc-500 uppercase tracking-wider">Tones</div>
                <div className="text-zinc-200 mt-0.5">{profile.phoneme_inventory.tones}</div>
              </div>
            </>
          )}

          <div>
            <div className="text-xs text-zinc-500 uppercase tracking-wider">WALS Features</div>
            <div className="text-zinc-200 mt-0.5 font-mono">{profile.wals_count}</div>
          </div>
          <div>
            <div className="text-xs text-zinc-500 uppercase tracking-wider">Grambank Features</div>
            <div className="text-zinc-200 mt-0.5 font-mono">{profile.grambank_count}</div>
          </div>
          <div>
            <div className="text-xs text-zinc-500 uppercase tracking-wider">Lexicon Entries</div>
            <div className="mt-0.5">
              {profile.lexicon_entries > 0 ? (
                <button
                  onClick={() => setShowLexicon(!showLexicon)}
                  className="text-teal-400 font-mono hover:text-teal-300 hover:underline"
                >
                  {profile.lexicon_entries.toLocaleString()}
                  <span className="text-xs ml-1">{showLexicon ? '▲' : '▼'}</span>
                </button>
              ) : (
                <span className="text-zinc-200 font-mono">0</span>
              )}
            </div>
          </div>
          <div>
            <div className="text-xs text-zinc-500 uppercase tracking-wider">Coordinates</div>
            <div className="text-zinc-200 mt-0.5 font-mono text-xs">
              {profile.coordinates.lat.toFixed(4)}, {profile.coordinates.lng.toFixed(4)}
            </div>
          </div>

          {profile.classification && (
            <div className="col-span-2 md:col-span-4">
              <div className="text-xs text-zinc-500 uppercase tracking-wider">Classification</div>
              <div className="text-zinc-400 mt-0.5 text-xs font-mono break-all">{profile.classification}</div>
            </div>
          )}

          {profile.phoneme_inventory && profile.phoneme_inventory.consonant_list.length > 0 && (
            <div className="col-span-2 md:col-span-4">
              <div className="text-xs text-zinc-500 uppercase tracking-wider mb-1">Consonant Inventory</div>
              <div className="flex flex-wrap gap-1">
                {profile.phoneme_inventory.consonant_list.map(c => (
                  <span key={c} className="px-1.5 py-0.5 bg-zinc-800 rounded text-xs text-zinc-300 font-mono">{c}</span>
                ))}
              </div>
            </div>
          )}

          {profile.phoneme_inventory && profile.phoneme_inventory.vowel_list.length > 0 && (
            <div className="col-span-2 md:col-span-4">
              <div className="text-xs text-zinc-500 uppercase tracking-wider mb-1">Vowel Inventory</div>
              <div className="flex flex-wrap gap-1">
                {profile.phoneme_inventory.vowel_list.map(v => (
                  <span key={v} className="px-1.5 py-0.5 bg-zinc-800 rounded text-xs text-zinc-300 font-mono">{v}</span>
                ))}
              </div>
            </div>
          )}

          {profile.phoneme_inventory && profile.phoneme_inventory.tone_list.length > 0 && (
            <div className="col-span-2 md:col-span-4">
              <div className="text-xs text-zinc-500 uppercase tracking-wider mb-1">Tone Inventory</div>
              <div className="flex flex-wrap gap-1">
                {profile.phoneme_inventory.tone_list.map(t => (
                  <span key={t} className="px-1.5 py-0.5 bg-zinc-800 rounded text-xs text-zinc-300 font-mono">{t}</span>
                ))}
              </div>
            </div>
          )}

          {showLexicon && (
            <div className="col-span-2 md:col-span-4">
              <LexiconBrowser code={code} name={profile.name} />
            </div>
          )}
        </div>
      )}
    </div>
  );
}

const LEVELS = ['A0', 'A1', 'A2', 'B1', 'B2', 'C1', 'C2'];
const BASE_URL = import.meta.env.VITE_API_BASE_URL ?? 'https://ojbr4cjpvh.execute-api.ap-northeast-1.amazonaws.com';

interface CefrDocMeta {
  key: string;
  language_code: string;
  level: string;
  status: 'draft' | 'approved';
  rating?: { overall: number } | null;
}

interface AtlasCefrSection {
  featureId: string;
  name: string;
  slug: string;
  level: string;
  order: number;
  status: string;
  sentenceCount: number;
}

interface LangCefrState {
  code: string;
  name: string;
  glottocode: string;
  docs: Record<string, CefrDocMeta>;
  atlasSections: AtlasCefrSection[];
  atlasLoaded: boolean;
}

function DataBuilderTab({ geo }: { geo: GeoJSON }) {
  const [langStates, setLangStates] = useState<LangCefrState[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState<'all' | 'has-approved' | 'has-atlas'>('all');
  const [expandedCode, setExpandedCode] = useState<string | null>(null);
  const [pulling, setPulling] = useState<string | null>(null);
  const [expunging, setExpunging] = useState<string | null>(null);
  const [pullingGrammar, setPullingGrammar] = useState<string | null>(null);
  const [pullResult, setPullResult] = useState<{ code: string; ok: boolean; message: string } | null>(null);

  const isoToGlotto = useMemo(() => {
    const map: Record<string, string> = {};
    for (const f of geo.features) {
      if (f.properties.c && f.properties.g) {
        map[f.properties.c.toUpperCase()] = f.properties.g;
      }
    }
    const COMPOUND: Record<string, string> = {
      CMN_CN: map['CMN'] || 'mand1415', CMN_TW: map['CMN'] || 'mand1415',
      YUE_HK: map['YUE'] || 'yuec1235', YUE_TS: map['YUE'] || 'yuec1235',
      SQI: map['SQI'] || 'alba1267',
      SPA_AR: map['SPA'] || 'stan1288', SPA_ES: map['SPA'] || 'stan1288', SPA_MX: map['SPA'] || 'stan1288',
      POR_BR: map['POR'] || 'port1283', POR_PT: map['POR'] || 'port1283',
      ENG_GB: map['ENG'] || 'stan1293', ENG_US: map['ENG'] || 'stan1293', ENG_AU: map['ENG'] || 'stan1293',
      HAK_SX: map['HAK'] || 'hakk1236', HAK_HL: map['HAK'] || 'hakk1236',
      NAN_TW: map['NAN'] || 'minn1241',
      WUU_SH: map['WUU'] || 'wuch1236', WUU_WZ: map['WUU'] || 'wuch1236',
      VIE_HN: map['VIE'] || 'viet1252', VIE_SG: map['VIE'] || 'viet1252',
      TAM_IN: map['TAM'] || 'tami1289',
    };
    Object.assign(map, COMPOUND);
    return map;
  }, [geo]);

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${BASE_URL}/api/admin/docs?_t=${Date.now()}`);
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const { docs } = await res.json() as { docs: CefrDocMeta[] };

        const byLang: Record<string, Record<string, CefrDocMeta>> = {};
        for (const d of docs) {
          if (!d.language_code || !d.level) continue;
          const lc = d.language_code;
          if (!byLang[lc]) byLang[lc] = {};
          byLang[lc]![d.level] = d;
        }

        const states: LangCefrState[] = Object.entries(byLang).map(([code, docMap]) => ({
          code,
          name: langName(code) || code,
          glottocode: isoToGlotto[code] || '',
          docs: docMap,
          atlasSections: [],
          atlasLoaded: false,
        }));

        states.sort((a, b) => a.name.localeCompare(b.name));
        setLangStates(states);
      } catch (err) {
        console.error('Failed to load CEFR docs:', err);
      } finally {
        setLoading(false);
      }
    })();
  }, [isoToGlotto]);

  const loadAtlasStatus = useCallback(async (code: string, glottocode: string) => {
    if (!glottocode) return;
    try {
      const data = await apiClient<{ sections: AtlasCefrSection[] }>(
        `/api/atlas/languages/${glottocode}/cefr`
      );
      setLangStates(prev => prev.map(ls =>
        ls.code === code ? { ...ls, atlasSections: data.sections || [], atlasLoaded: true } : ls
      ));
    } catch {
      setLangStates(prev => prev.map(ls =>
        ls.code === code ? { ...ls, atlasSections: [], atlasLoaded: true } : ls
      ));
    }
  }, []);

  const handleExpand = useCallback((code: string) => {
    if (expandedCode === code) {
      setExpandedCode(null);
      return;
    }
    setExpandedCode(code);
    const ls = langStates.find(l => l.code === code);
    if (ls && !ls.atlasLoaded && ls.glottocode) {
      loadAtlasStatus(code, ls.glottocode);
    }
  }, [expandedCode, langStates, loadAtlasStatus]);

  const handlePull = useCallback(async (code: string, levels: string[]) => {
    setPulling(code);
    setPullResult(null);
    try {
      const ls = langStates.find(l => l.code === code);
      const glottocode = ls?.glottocode || isoToGlotto[code] || '';
      const result = await apiClient<{ imported: number; message: string }>(
        '/api/admin/atlas/cefr/import',
        {
          method: 'POST',
          body: JSON.stringify({ language_code: code, glottocode, levels }),
        }
      );
      setPullResult({ code, ok: true, message: result.message || `Imported ${result.imported} sections` });
      if (glottocode) loadAtlasStatus(code, glottocode);
    } catch (err) {
      setPullResult({ code, ok: false, message: err instanceof Error ? err.message : 'Import failed' });
    } finally {
      setPulling(null);
    }
  }, [langStates, isoToGlotto, loadAtlasStatus]);

  const handleExpunge = useCallback(async (code: string) => {
    if (!confirm(`Expunge all CEFR data for ${langName(code) || code} from Atlas?`)) return;
    setExpunging(code);
    setPullResult(null);
    try {
      const ls = langStates.find(l => l.code === code);
      const glottocode = ls?.glottocode || isoToGlotto[code] || '';
      const result = await apiClient<{ deleted: number; message: string }>(
        '/api/admin/atlas/cefr/expunge',
        {
          method: 'POST',
          body: JSON.stringify({ language_code: code, glottocode }),
        }
      );
      setPullResult({ code, ok: true, message: result.message || `Expunged ${result.deleted} records` });
      if (glottocode) loadAtlasStatus(code, glottocode);
    } catch (err) {
      setPullResult({ code, ok: false, message: err instanceof Error ? err.message : 'Expunge failed' });
    } finally {
      setExpunging(null);
    }
  }, [langStates, isoToGlotto, loadAtlasStatus]);

  const handleGrammarPull = useCallback(async (code: string) => {
    setPullingGrammar(code);
    setPullResult(null);
    try {
      const ls = langStates.find(l => l.code === code);
      const glottocode = ls?.glottocode || isoToGlotto[code] || '';
      const result = await apiClient<{ pulled: number; skipped: number; total: number; message: string }>(
        '/api/admin/atlas/grammar/pull',
        {
          method: 'POST',
          body: JSON.stringify({ language_code: code, glottocode }),
        }
      );
      setPullResult({ code, ok: true, message: result.message || `Pulled ${result.pulled} grammar patterns` });
    } catch (err) {
      setPullResult({ code, ok: false, message: err instanceof Error ? err.message : 'Grammar pull failed' });
    } finally {
      setPullingGrammar(null);
    }
  }, [langStates, isoToGlotto]);

  const [generatingQuestions, setGeneratingQuestions] = useState<string | null>(null);
  const [questionProgress, setQuestionProgress] = useState<{ withQuestions: number; total: number; percent: number } | null>(null);

  const handleGenerateQuestions = useCallback(async (code: string, regenerate = false) => {
    setGeneratingQuestions(code);
    setQuestionProgress({ withQuestions: 0, total: 0, percent: 0 });
    setPullResult(null);
    try {
      let totalGenerated = 0;
      let remaining = 1;
      while (remaining > 0) {
        const result = await apiClient<{ done: boolean; generated: number; total: number; withQuestions: number; remaining: number }>(
          '/api/admin/atlas/cefr/generate-questions',
          { method: 'POST', body: JSON.stringify({ language_code: code, batch_size: 5, regenerate }) }
        );
        totalGenerated += result.generated;
        remaining = result.remaining;
        const percent = result.total > 0 ? Math.round((result.withQuestions / result.total) * 100) : 0;
        setQuestionProgress({ withQuestions: result.withQuestions, total: result.total, percent });
        if (result.done) break;
      }
      const msg = totalGenerated === 0
        ? `All sections already have questions — nothing to generate.`
        : `Done — generated questions for ${totalGenerated} sections.`;
      setPullResult({ code, ok: true, message: msg });
    } catch (err) {
      setPullResult({ code, ok: false, message: err instanceof Error ? err.message : 'Question generation failed' });
    } finally {
      setGeneratingQuestions(null);
      setQuestionProgress(null);
    }
  }, [questionProgress?.total]);

  const filtered = useMemo(() => {
    const q = search.toLowerCase().trim();
    return langStates.filter(ls => {
      if (q && !ls.name.toLowerCase().includes(q) && !ls.code.toLowerCase().includes(q)) return false;
      if (filterStatus === 'has-approved') {
        return Object.values(ls.docs).some(d => d.status === 'approved');
      }
      if (filterStatus === 'has-atlas') {
        return ls.atlasLoaded && ls.atlasSections.length > 0;
      }
      return true;
    });
  }, [langStates, search, filterStatus]);

  const summary = useMemo(() => {
    let totalLangs = langStates.length;
    let withApproved = 0;
    let totalDocs = 0;
    let approvedDocs = 0;
    for (const ls of langStates) {
      const docs = Object.values(ls.docs);
      totalDocs += docs.length;
      const approved = docs.filter(d => d.status === 'approved');
      approvedDocs += approved.length;
      if (approved.length > 0) withApproved++;
    }
    return { totalLangs, withApproved, totalDocs, approvedDocs };
  }, [langStates]);

  if (loading) {
    return <p className="text-zinc-500 text-sm py-8">Loading CEFR docs...</p>;
  }

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div className="border border-zinc-800 rounded-lg p-3">
          <div className="text-xs uppercase tracking-wider text-zinc-500">Languages with Docs</div>
          <div className="text-2xl font-semibold text-white mt-1">{summary.totalLangs}</div>
        </div>
        <div className="border border-zinc-800 rounded-lg p-3">
          <div className="text-xs uppercase tracking-wider text-zinc-500">With Approved Docs</div>
          <div className="text-2xl font-semibold text-teal-400 mt-1">{summary.withApproved}</div>
        </div>
        <div className="border border-zinc-800 rounded-lg p-3">
          <div className="text-xs uppercase tracking-wider text-zinc-500">Total Docs</div>
          <div className="text-2xl font-semibold text-white mt-1">{summary.totalDocs}</div>
        </div>
        <div className="border border-zinc-800 rounded-lg p-3">
          <div className="text-xs uppercase tracking-wider text-zinc-500">Approved Docs</div>
          <div className="text-2xl font-semibold text-teal-400 mt-1">{summary.approvedDocs}</div>
        </div>
      </div>

      <div className="flex flex-wrap gap-3">
        <input
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder="Search by name or code..."
          className="w-full sm:w-64 px-3 py-1.5 bg-zinc-800 border border-zinc-700 rounded text-sm text-white placeholder-zinc-500 focus:border-teal-600 focus:outline-none"
        />
        <select
          value={filterStatus}
          onChange={e => setFilterStatus(e.target.value as typeof filterStatus)}
          className="w-full sm:w-auto px-3 py-1.5 bg-zinc-800 border border-zinc-700 rounded text-sm text-white focus:border-teal-600 focus:outline-none"
        >
          <option value="all">All languages</option>
          <option value="has-approved">Has approved docs</option>
          <option value="has-atlas">Has Atlas data</option>
        </select>
      </div>

      <div className="text-xs text-zinc-500">{filtered.length} languages</div>

      <div className="space-y-1">
        {filtered.map(ls => {
          const isExpanded = expandedCode === ls.code;
          const approvedLevels = LEVELS.filter(lv => ls.docs[lv]?.status === 'approved');
          const draftLevels = LEVELS.filter(lv => ls.docs[lv]?.status === 'draft');
          return (
            <div key={ls.code} className="border border-zinc-800 rounded-lg overflow-hidden">
              <button
                onClick={() => handleExpand(ls.code)}
                className="w-full text-left px-4 py-3 flex flex-wrap items-center gap-3 hover:bg-zinc-800/30 transition-colors"
              >
                <span className="text-teal-400 font-mono text-sm w-16 shrink-0">{ls.code}</span>
                <span className="text-zinc-200 text-sm font-medium min-w-[120px]">{ls.name}</span>
                <div className="flex gap-1 flex-wrap">
                  {LEVELS.map(lv => {
                    const doc = ls.docs[lv];
                    if (!doc) return (
                      <span key={lv} className="w-7 h-5 rounded text-[10px] flex items-center justify-center bg-zinc-800/50 text-zinc-600">{lv}</span>
                    );
                    const isApproved = doc.status === 'approved';
                    const rating = doc.rating?.overall;
                    return (
                      <span
                        key={lv}
                        className={`w-7 h-5 rounded text-[10px] flex items-center justify-center font-medium ${
                          isApproved
                            ? 'bg-teal-900/40 text-teal-400 border border-teal-800/50'
                            : 'bg-amber-900/30 text-amber-400 border border-amber-800/50'
                        }`}
                        title={`${lv}: ${doc.status}${rating ? ` (${rating}/10)` : ''}`}
                      >
                        {lv}
                      </span>
                    );
                  })}
                </div>
                {ls.atlasLoaded && ls.atlasSections.length > 0 && (
                  <span className="text-xs bg-teal-900/30 text-teal-400 border border-teal-800/40 rounded px-1.5 py-0.5">
                    {ls.atlasSections.length} sections in Atlas
                  </span>
                )}
                <svg
                  className={`w-4 h-4 text-zinc-500 ml-auto shrink-0 transition-transform ${isExpanded ? 'rotate-180' : ''}`}
                  fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}
                >
                  <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                </svg>
              </button>

              {isExpanded && (
                <DataBuilderDetail
                  ls={ls}
                  approvedLevels={approvedLevels}
                  draftLevels={draftLevels}
                  pulling={pulling === ls.code}
                  expunging={expunging === ls.code}
                  pullingGrammar={pullingGrammar === ls.code}
                  pullResult={pullResult?.code === ls.code ? pullResult : null}
                  onPull={(levels) => handlePull(ls.code, levels)}
                  onExpunge={() => handleExpunge(ls.code)}
                  onGrammarPull={() => handleGrammarPull(ls.code)}
                  generatingQuestions={generatingQuestions === ls.code}
                  questionProgress={questionProgress}
                  onGenerateQuestions={(regen) => handleGenerateQuestions(ls.code, regen)}
                />
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

interface VoiceEntry { voiceId: string; provider: string; language: string; gender: string }
interface VoiceConfig {
  voiceEnabled: boolean;
  voices: Record<string, VoiceEntry> | null;
  defaults: Record<string, VoiceEntry> | null;
}

function VoiceSection({ code }: { code: string }) {
  const [config, setConfig] = useState<VoiceConfig | null>(null);
  const [loading, setLoading] = useState(true);
  const [toggling, setToggling] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        const res = await fetch(`${BASE_URL}/api/v1/atlas/${code.toLowerCase()}/voice`);
        if (res.ok) {
          const data = await res.json();
          setConfig({ voiceEnabled: data.voiceEnabled, voices: data.voices, defaults: data.defaults });
        } else {
          setConfig({ voiceEnabled: false, voices: null, defaults: null });
        }
      } catch {
        setConfig({ voiceEnabled: false, voices: null, defaults: null });
      } finally {
        setLoading(false);
      }
    })();
  }, [code]);

  const toggleVoice = async (enable: boolean) => {
    setToggling(true);
    setError(null);
    try {
      const res = await fetch(`${BASE_URL}/api/admin/atlas/voice/config`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ language_code: code, enabled: enable }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`);
      setConfig(prev => ({ ...prev!, voiceEnabled: data.voiceEnabled, voices: data.voices }));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to toggle voice');
    } finally {
      setToggling(false);
    }
  };

  if (loading) return <p className="text-zinc-500 text-xs">Loading voice config...</p>;

  const displayVoices = config?.voiceEnabled ? config.voices : config?.defaults;
  const hasVoices = displayVoices && Object.keys(displayVoices).length > 0;

  return (
    <div>
      <h4 className="text-xs uppercase tracking-wider text-zinc-500 mb-2">Voice (TTS)</h4>
      {hasVoices && (
        <div className="space-y-1 mb-2">
          {Object.entries(displayVoices!).map(([gender, v]) => (
            <div key={gender} className="flex items-center gap-2 text-sm">
              <span className={`w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold ${
                gender === 'male' ? 'bg-blue-500/20 text-blue-400' : 'bg-pink-500/20 text-pink-400'
              }`}>{gender === 'male' ? 'M' : 'F'}</span>
              <span className="text-zinc-300 font-mono text-xs">{v.voiceId}</span>
              <span className="text-zinc-600 text-xs">({v.provider})</span>
              {!config?.voiceEnabled && (
                <span className="text-zinc-600 text-[10px] uppercase">available</span>
              )}
            </div>
          ))}
        </div>
      )}
      {!hasVoices && !config?.voiceEnabled && (
        <p className="text-zinc-600 text-xs mb-2">No default voices configured for this language</p>
      )}
      <div className="flex items-center gap-3">
        <button
          onClick={() => toggleVoice(!config?.voiceEnabled)}
          disabled={toggling || (!hasVoices && !config?.voiceEnabled)}
          className={`relative w-10 h-5 rounded-full transition-colors ${
            config?.voiceEnabled ? 'bg-teal-600' : 'bg-zinc-700'
          } ${toggling || (!hasVoices && !config?.voiceEnabled) ? 'opacity-30' : ''}`}
        >
          <span className={`absolute top-0.5 left-0.5 w-4 h-4 rounded-full bg-white transition-transform ${
            config?.voiceEnabled ? 'translate-x-5' : ''
          }`} />
        </button>
        <span className={`text-sm ${config?.voiceEnabled ? 'text-teal-400' : 'text-zinc-500'}`}>
          {toggling ? 'Updating...' : config?.voiceEnabled ? 'Enabled' : 'Disabled'}
        </span>
      </div>
      {error && <p className="text-red-400 text-xs mt-1">{error}</p>}
    </div>
  );
}

function DataBuilderDetail({ ls, approvedLevels, draftLevels, pulling, expunging, pullingGrammar, pullResult, onPull, onExpunge, onGrammarPull, generatingQuestions, questionProgress, onGenerateQuestions }: {
  ls: LangCefrState;
  approvedLevels: string[];
  draftLevels: string[];
  pulling: boolean;
  expunging: boolean;
  pullingGrammar: boolean;
  pullResult: { ok: boolean; message: string } | null;
  onPull: (levels: string[]) => void;
  onExpunge: () => void;
  onGrammarPull: () => void;
  generatingQuestions: boolean;
  questionProgress: { withQuestions: number; total: number; percent: number } | null;
  onGenerateQuestions: (regenerate?: boolean) => void;
}) {
  const [selectedLevels, setSelectedLevels] = useState<Set<string>>(new Set(approvedLevels));

  const toggleLevel = (lv: string) => {
    setSelectedLevels(prev => {
      const next = new Set(prev);
      if (next.has(lv)) next.delete(lv);
      else next.add(lv);
      return next;
    });
  };

  return (
    <div className="px-4 pb-4 space-y-4 border-t border-zinc-800">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-4">
        {/* Doc status */}
        <div>
          <h4 className="text-xs uppercase tracking-wider text-zinc-500 mb-2">CEFR Documents</h4>
          <div className="space-y-1">
            {LEVELS.map(lv => {
              const doc = ls.docs[lv];
              if (!doc) return (
                <div key={lv} className="flex items-center gap-3 text-sm py-1">
                  <span className="font-mono text-zinc-600 w-6">{lv}</span>
                  <span className="text-zinc-600">No document</span>
                </div>
              );
              const rating = doc.rating?.overall;
              return (
                <div key={lv} className="flex items-center gap-3 text-sm py-1">
                  <span className="font-mono text-zinc-300 w-6">{lv}</span>
                  <span className={doc.status === 'approved'
                    ? 'text-teal-400' : 'text-amber-400'
                  }>
                    {doc.status}
                  </span>
                  {rating != null && (
                    <span className={`font-mono text-xs ${
                      rating >= 8 ? 'text-teal-400' : rating >= 6 ? 'text-amber-400' : 'text-red-400'
                    }`}>
                      {rating}/10
                    </span>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Atlas status */}
        <div>
          <h4 className="text-xs uppercase tracking-wider text-zinc-500 mb-2">Atlas Import Status</h4>
          {!ls.atlasLoaded ? (
            <p className="text-zinc-500 text-sm">Loading...</p>
          ) : ls.atlasSections.length === 0 ? (
            <p className="text-zinc-500 text-sm">No CEFR data in Atlas</p>
          ) : (
            <div className="space-y-1 max-h-[200px] overflow-y-auto">
              {ls.atlasSections.map(s => (
                <div key={s.featureId} className="flex items-center gap-2 text-sm py-0.5">
                  <span className="font-mono text-xs text-zinc-500 w-6">{s.level}</span>
                  <span className="text-zinc-300 truncate">{s.name}</span>
                  <span className="text-zinc-600 text-xs ml-auto">{s.sentenceCount} sent.</span>
                </div>
              ))}
            </div>
          )}

          {ls.glottocode && (
            <p className="text-xs text-zinc-600 mt-2">Glottocode: {ls.glottocode}</p>
          )}
          {!ls.glottocode && (
            <p className="text-xs text-red-400 mt-2">No glottocode mapping — cannot import</p>
          )}
        </div>
      </div>

      {/* Voice Management */}
      {ls.glottocode && (
        <div className="border-t border-zinc-800 pt-4">
          <VoiceSection code={ls.code} />
        </div>
      )}

      {/* Actions */}
      <div className="border-t border-zinc-800 pt-4">
        <h4 className="text-xs uppercase tracking-wider text-zinc-500 mb-3">Actions</h4>

        {approvedLevels.length > 0 && ls.glottocode && (
          <div className="flex flex-wrap items-center gap-3 mb-3">
            <span className="text-sm text-zinc-400">Pull levels:</span>
            {approvedLevels.map(lv => (
              <label key={lv} className="flex items-center gap-1.5 cursor-pointer">
                <input
                  type="checkbox"
                  checked={selectedLevels.has(lv)}
                  onChange={() => toggleLevel(lv)}
                  className="rounded border-zinc-600 bg-zinc-800 text-teal-500 focus:ring-teal-600"
                />
                <span className="text-sm text-zinc-300 font-mono">{lv}</span>
              </label>
            ))}
            <button
              onClick={() => onPull([...selectedLevels])}
              disabled={pulling || selectedLevels.size === 0}
              className="px-3 py-1.5 bg-teal-700 rounded text-sm text-white hover:bg-teal-600 disabled:opacity-30 flex items-center gap-2"
            >
              {pulling ? (
                <>
                  <span className="w-3 h-3 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  Importing...
                </>
              ) : (
                <>Pull to Atlas</>
              )}
            </button>
          </div>
        )}

        {approvedLevels.length === 0 && (
          <p className="text-sm text-zinc-500 mb-3">No approved documents to pull.</p>
        )}

        {ls.glottocode && (
          <div className="flex flex-wrap items-center gap-3 mb-3">
            <button
              onClick={onGrammarPull}
              disabled={pullingGrammar}
              className="px-3 py-1.5 bg-indigo-700 rounded text-sm text-white hover:bg-indigo-600 disabled:opacity-30 flex items-center gap-2"
            >
              {pullingGrammar ? (
                <>
                  <span className="w-3 h-3 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  Pulling Grammar...
                </>
              ) : (
                <>Pull Grammar Patterns</>
              )}
            </button>
            <div className="flex flex-col gap-1.5">
              <div className="flex items-center gap-2 flex-wrap">
                <button
                  onClick={() => onGenerateQuestions(false)}
                  disabled={generatingQuestions}
                  className="px-3 py-1.5 bg-violet-700 rounded text-sm text-white hover:bg-violet-600 disabled:opacity-30 flex items-center gap-2 shrink-0"
                >
                  {generatingQuestions ? (
                    <>
                      <span className="w-3 h-3 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                      Generating...
                    </>
                  ) : (
                    <>Generate SEO Questions</>
                  )}
                </button>
                <button
                  onClick={() => onGenerateQuestions(true)}
                  disabled={generatingQuestions}
                  className="px-3 py-1.5 bg-zinc-700 rounded text-sm text-zinc-300 hover:bg-zinc-600 disabled:opacity-30 flex items-center gap-2 shrink-0"
                >
                  Regenerate All
                </button>
              </div>
              {generatingQuestions && questionProgress && (
                <div className="flex items-center gap-2">
                  <div className="flex-1 h-2 bg-zinc-800 rounded-full overflow-hidden max-w-[200px]">
                    <div
                      className="h-full bg-violet-500 rounded-full transition-all duration-300"
                      style={{ width: `${questionProgress.percent}%` }}
                    />
                  </div>
                  <span className="text-xs text-zinc-400 font-mono tabular-nums">
                    {questionProgress.percent}% ({questionProgress.withQuestions}/{questionProgress.total})
                  </span>
                </div>
              )}
            </div>
          </div>
        )}

        {ls.atlasLoaded && ls.atlasSections.length > 0 && (
          <button
            onClick={onExpunge}
            disabled={expunging}
            className="px-3 py-1.5 bg-red-900/50 border border-red-800/50 rounded text-sm text-red-400 hover:bg-red-900/70 disabled:opacity-30 flex items-center gap-2"
          >
            {expunging ? (
              <>
                <span className="w-3 h-3 border-2 border-red-400/30 border-t-red-400 rounded-full animate-spin" />
                Expunging...
              </>
            ) : (
              <>Expunge Atlas Data</>
            )}
          </button>
        )}

        {pullResult && (
          <div className={`mt-3 text-sm px-3 py-2 rounded ${
            pullResult.ok
              ? 'bg-teal-900/20 border border-teal-800/40 text-teal-400'
              : 'bg-red-900/20 border border-red-800/40 text-red-400'
          }`}>
            {pullResult.message}
          </div>
        )}
      </div>
    </div>
  );
}
